import React from "react";
import { useFormikContext, Field, ErrorMessage } from "formik";
import { InferenceBlockEditor } from "./InferenceBlockEditor";
import InputSettings from "./InputSettings/index.jsx";
import { Container } from "./styles";
import {
  BLOCK_TYPES,
  getDefaultBasicPlugin,
  addUserTextInputOption,
  removeUserTextInputOption,
  getLiquidReferencedVariables,
  normalizeReferencedVariable,
  PluginStatus,
  PluginPromptVisibility,
} from "../../../../../src/shared/schemas/PluginSchema";
import { PROMPT_1_RESULT_NAME } from "./utils";
import { Textarea } from "../../../sudokit/components/Textarea";
import { Input } from "../../../sudokit/components/Input";
import StyledSelect from "../../StyledSelect";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import { selectFocusStyle } from "../../../styles/a11y";
import { getDefaultDocumentInputOutput } from "./inputSettings";

const defaultPlugin = getDefaultBasicPlugin();
const defaultInferenceBlock = defaultPlugin.blocks.find((b) => b.blockType === "inference");
const documentVariables = ["preceding_text", "following_text", "highlighted_text", "previous_document_text", "documentId"];
const pluginStatusOptions = [
  {
    value: PluginStatus.PUBLISHED,
    label: "Everyone",
    description: "All Sudowrite users can see your plugin",
  },
  {
    value: PluginStatus.UNLISTED,
    label: "Unlisted",
    description: "Only those with the plugin link can see your plugin",
  },
];

const pluginInstructionVisibilityOptions = [
  {
    value: PluginPromptVisibility.HIDDEN,
    label: "Only You",
    description: "Keep your secret sauce hidden ;)",
  },
  {
    value: PluginPromptVisibility.VISIBLE,
    label: "Anyone that can see your plugin",
    description: "Helps the community improve together",
  },
];

const metaSelectStyleOverrides = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    ...selectFocusStyle(state),
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
  }),
  option: (base, state) => ({
    ...base,
    color: "var(--grey-0)",
    backgroundColor: state.isFocused || state.isSelected ? "var(--menu-color-hover)" : "var(--menu-color)",
    padding: "9px 12px",
  }),
};
const metaLabelAlignmentClassNames = {
  control: "metaLabel--align-control",
  audience: "metaLabel--align-audience",
  category: "metaLabel--align-category",
};

const formatMetaSelectOptionLabel = (option, { context }) => {
  if (context === "value" || !option.description) {
    return option.label;
  }

  return (
    <div className="metaSelectOptionContent">
      <span className="metaSelectOptionLabel">{option.label}</span>
      <span className="metaSelectOptionDescription">{option.description}</span>
    </div>
  );
};

export default function AdvancedEditor({ categories }) {
  const { values, setFieldValue, setValues } = useFormikContext();

  const inputBlock = values.blocks.find((b) => b.blockType === "input_document");
  const outputBlock = values.blocks.find((b) => b.blockType === "card_output");
  const inferenceBlocks = values.blocks.filter((b) => b.blockType === "inference");
  const promptTemplateSignature = inferenceBlocks
    .map((block) => block.options?.prompt?.messages?.map((msg) => msg.template).join(""))
    .join("");
  const hasUserTextInputOption = Array.isArray(inputBlock?.options?.outputs)
    && inputBlock.options.outputs.some((output) => output.source === "user_text_input");

  const usesUserTextInputInPrompts = (pluginValues) => {
    const inferenceBlocksToInspect = (pluginValues?.blocks || []).filter(
      (block) => block.blockType === "inference"
    );
    for (const block of inferenceBlocksToInspect) {
      const messages = block.options?.prompt?.messages || [];
      for (const message of messages) {
        if (!message?.template) continue;
        const variables = getLiquidReferencedVariables(message.template);
        const hasUserTextInput = variables.some((variable) => {
          const normalized = normalizeReferencedVariable(variable);
          return normalized === "user_text_input";
        });
        if (hasUserTextInput) return true;
      }
    }
    return false;
  };

  const normalizeBlocksForUserTextInputOption = (pluginValues) => ({
    ...pluginValues,
    connections: Array.isArray(pluginValues.connections) ? pluginValues.connections : [],
    blocks: (pluginValues.blocks || []).map((block) => {
      if (block.blockType === "input_document") {
        return {
          ...block,
          outputs: Array.isArray(block.outputs) ? block.outputs : [],
          options: {
            ...(block.options || {}),
            outputs: Array.isArray(block.options?.outputs) ? block.options.outputs : [],
          },
        };
      }
      if (block.blockType === "inference") {
        return {
          ...block,
          inputs: Array.isArray(block.inputs) ? block.inputs : [],
        };
      }
      return block;
    }),
  });

  // Function to detect which document input variables are used in any inference block prompts
  // This is used to ensure that we show the correct input value blocks in the UI based
  // on the variables used in the inference block prompts.
  const detectDocumentInputUsage = () => {
    const usedVariables = new Set();

    for (const block of inferenceBlocks) {
      if (block.options?.prompt?.messages) {
        for (const message of block.options.prompt.messages) {
          if (message.template) {
            const variables = getLiquidReferencedVariables(message.template);
            variables.forEach((variable) => {
              const normalized = normalizeReferencedVariable(variable);
              if (documentVariables.includes(normalized)) {
                usedVariables.add(normalized);
              }
            });
          }
        }
      }
    }

    return Array.from(usedVariables);
  };

  // Function to ensure document input outputs are added to or removed from input document block
  // Necessary because we dynamically add and remove outputs from the input document block based
  // on the variables used in the inference block prompts.
  const ensureDocumentInputOutputs = () => {
    if (!inputBlock) return;

    const usedVariables = detectDocumentInputUsage();
    const currentOutputs = inputBlock.options?.outputs || [];
    const currentOutputSources = currentOutputs.map((output) => output.source);

    const variableConfigs = {
      preceding_text: getDefaultDocumentInputOutput("preceding_text"),
      following_text: getDefaultDocumentInputOutput("following_text"),
      highlighted_text: getDefaultDocumentInputOutput("highlighted_text"),
      previous_document_text: getDefaultDocumentInputOutput("previous_document_text"),
      documentId: { source: "documentId", target: "documentId" },
    };

    const currentBlockOutputs = Array.isArray(inputBlock.outputs) ? inputBlock.outputs : [];

    const outputsToAdd = usedVariables.filter((variable) => !currentOutputSources.includes(variable));
    const outputsToRemove = currentOutputSources.filter((source) => documentVariables.includes(source) && !usedVariables.includes(source));

    // Block-level outputs that need backfilling (option exists but block output is missing)
    const optionDocumentSources = currentOutputSources.filter((source) => documentVariables.includes(source));
    const requiredBlockOutputs = Array.from(new Set([...optionDocumentSources, ...usedVariables]));
    const blockOutputsToAdd = requiredBlockOutputs
      .filter((variable) => !currentBlockOutputs.some((output) => output.name === variable))
      .map((variable) => ({ name: variable, type: variable === "documentId" ? "documentId" : "text" }));

    if (outputsToAdd.length === 0 && outputsToRemove.length === 0 && blockOutputsToAdd.length === 0) return;

    // Build a single working copy of blocks and connections so additions and
    // removals never clobber each other via competing setFieldValue calls.
    const workingBlocks = [...values.blocks];
    let workingConnections = [...values.connections];
    const inputBlockIndex = workingBlocks.findIndex((b) => b.blockType === "input_document");

    // --- Additions: option outputs, block outputs, inference inputs, connections ---
    let updatedOptionOutputs = [...currentOutputs];
    let updatedBlockOutputs = [...currentBlockOutputs, ...blockOutputsToAdd];

    if (outputsToAdd.length > 0) {
      updatedOptionOutputs = [...updatedOptionOutputs, ...outputsToAdd.map((variable) => variableConfigs[variable])];

      const newBlockOutputsFromAdd = outputsToAdd
        .filter((variable) => !updatedBlockOutputs.some((output) => output.name === variable))
        .map((variable) => ({ name: variable, type: variable === "documentId" ? "documentId" : "text" }));
      updatedBlockOutputs = [...updatedBlockOutputs, ...newBlockOutputsFromAdd];

      // Add inputs to every inference block and create connections
      workingBlocks.forEach((block, idx) => {
        if (block.blockType !== "inference") return;
        let blockInputs = block.inputs || [];
        let inputsChanged = false;

        outputsToAdd.forEach((variable) => {
          workingConnections.push({
            source: inputBlock.id,
            sourcePath: variable,
            target: block.id,
            targetPath: variable,
          });

          if (!blockInputs.some((input) => input.name === variable)) {
            blockInputs = [...blockInputs, { name: variable, type: variable === "documentId" ? "documentId" : "text" }];
            inputsChanged = true;
          }
        });

        if (inputsChanged) {
          workingBlocks[idx] = { ...block, inputs: blockInputs };
        }
      });
    }


    if (outputsToRemove.length > 0) {
      updatedOptionOutputs = updatedOptionOutputs.filter((output) => !outputsToRemove.includes(output.source));
      updatedBlockOutputs = updatedBlockOutputs.filter((output) => !outputsToRemove.includes(output.name));

      workingBlocks.forEach((block, idx) => {
        if (block.blockType !== "inference") return;
        const filtered = block.inputs?.filter((input) => !outputsToRemove.includes(input.name)) || [];
        if (filtered.length !== (block.inputs?.length ?? 0)) {
          workingBlocks[idx] = { ...block, inputs: filtered };
        }
      });

      workingConnections = workingConnections.filter(
        (connection) => !(outputsToRemove.includes(connection.sourcePath) && connection.source === inputBlock.id)
      );
    }

    // --- Apply the updated input block once ---
    workingBlocks[inputBlockIndex] = {
      ...inputBlock,
      options: { ...inputBlock.options, outputs: updatedOptionOutputs },
      outputs: updatedBlockOutputs,
    };

    setFieldValue("blocks", workingBlocks);
    setFieldValue("connections", workingConnections);
  };

  // Check for document input variable usage whenever inference blocks change
  React.useEffect(() => {
    // Only run if we have inference blocks and an input document block
    if (inferenceBlocks.length > 0 && inputBlock) {
      ensureDocumentInputOutputs();
    }
  }, [promptTemplateSignature, inputBlock?.options?.outputs?.map((output) => output.source).join("")]);

  // user_text_input should follow prompt usage automatically:
  // referenced => option enabled, not referenced => option disabled.
  React.useEffect(() => {
    if (!inputBlock) return;
    const shouldEnableUserTextInput = usesUserTextInputInPrompts(values);
    if (shouldEnableUserTextInput === hasUserTextInputOption) {
      return;
    }
    setValues((previousValues) => {
      const normalizedValues = normalizeBlocksForUserTextInputOption(previousValues);
      const previousInputDocumentBlock = normalizedValues.blocks.find(
        (block) => block.blockType === "input_document"
      );
      const previousHasUserTextInputOption =
        Array.isArray(previousInputDocumentBlock?.options?.outputs)
        && previousInputDocumentBlock.options.outputs.some(
          (output) => output.source === "user_text_input"
        );
      const shouldEnableUserTextInputFromPreviousValues =
        usesUserTextInputInPrompts(previousValues);
      if (
        previousHasUserTextInputOption
        === shouldEnableUserTextInputFromPreviousValues
      ) {
        return previousValues;
      }
      if (shouldEnableUserTextInputFromPreviousValues) {
        return addUserTextInputOption(normalizedValues);
      }
      return removeUserTextInputOption(normalizedValues);
    });
  }, [hasUserTextInputOption, promptTemplateSignature]);

  const addPromptStage = (e) => {
    e.preventDefault();

    // create the block.
    const newBlock = {
      ...defaultInferenceBlock,
      id: "prompt-2",
      inputs: [
        ...inferenceBlocks[0].inputs,
        {
          name: PROMPT_1_RESULT_NAME,
          type: "text",
        },
      ],
    };

    // update the second prompt to show how to utilize the new input variable
    newBlock.options.prompt.messages[1].template = `This was the output of the first prompt: \n\n{{ ${PROMPT_1_RESULT_NAME} }}\n\nNow make it even more original and fresh.`;

    const newBlocks = values.blocks.reduce((acc, block) => {
      if (block.id === inferenceBlocks[0].id) {
        const updatedPrompt1 = {
          ...block,
          options: {
            ...block.options,
            stream: false,
            numGenerations: 1,
            outputName: PROMPT_1_RESULT_NAME,
          },
        };

        return [...acc, updatedPrompt1, newBlock];
      }
      return [...acc, block];
    }, []);

    setFieldValue("blocks", newBlocks);

    // remove the connections from prompt-1 to output-1
    // replace them with prompt-2 to output-1
    const prompt1ConnectionsRemoved = values.connections.filter((connection) => connection.source !== inferenceBlocks[0].id);
    // copy all the input connections for target prompt-1
    // to target prompt-2 as well.  If prompt-1 has it available, prompt 2 will too.
    const prompt1TargetConnections = values.connections.filter((connection) => connection.target === inferenceBlocks[0].id);
    const newConnections = [
      ...prompt1ConnectionsRemoved,
      ...prompt1TargetConnections.map((connection) => ({
        ...connection,
        target: "prompt-2",
      })),
      {
        source: inputBlock.id, // input block
        sourcePath: "highlighted_text",
        target: "prompt-2",
        targetPath: "highlighted_text",
      },
      {
        source: inferenceBlocks[0].id,
        sourcePath: "generations",
        target: "prompt-2",
        targetPath: PROMPT_1_RESULT_NAME,
      },
      {
        source: "prompt-2",
        sourcePath: "generations",
        target: outputBlock.id,
        targetPath: "content",
      },
      {
        source: "prompt-2",
        sourcePath: "completionId",
        target: outputBlock.id,
        targetPath: "completionId",
      },
    ];

    setFieldValue("connections", newConnections);
  };
  const removeSecondStage = () => {
    const newBlocks = values.blocks
      .filter((block) => block.id !== "prompt-2")
      .map((block) => {
        if (block.blockType === BLOCK_TYPES.INFERENCE) {
          block.options.stream = true;
        }
        return block;
      });
    setFieldValue("blocks", newBlocks);
    const connectionsWithoutPrompt2 = values.connections.filter((connection) => connection.source !== "prompt-2" && connection.target !== "prompt-2");
    const newConnections = [
      ...connectionsWithoutPrompt2,
      {
        source: inferenceBlocks[0].id,
        sourcePath: "generations",
        target: outputBlock.id,
        targetPath: "content",
      },
      {
        source: inferenceBlocks[0].id,
        sourcePath: "completionId",
        target: outputBlock.id,
        targetPath: "completionId",
      },
    ];
    setFieldValue("connections", newConnections);
  };

  let inferenceBlockNumber = 0;

  return (
    <Container>
      <div className="meta flex flex-col gap-4">
        <div className="metaRow">
          <h3 id="edit-plugin-name-label" className="metaLabel">Plugin Name</h3>
          <Field name="name">
            {({ field, meta }) => (
              <label htmlFor="edit-plugin-name-input" className="metaField">
                <Input
                  id="edit-plugin-name-input"
                  type="text"
                  error={meta.error && meta.touched}
                  aria-labelledby="edit-plugin-name-label"
                  aria-invalid={Boolean(meta.error && meta.touched)}
                  placeholder="e.g., Reader Reaction Simulator"
                  {...field}
                />
                <ErrorMessage
                  name="name"
                  component="p"
                  className="error"
                />
              </label>
            )}
          </Field>
        </div>

        <div className="metaRow metaRow--top">
          <h3 id="edit-plugin-description-label" className={`metaLabel ${metaLabelAlignmentClassNames.control}`}>Plugin Description</h3>
          <Field name="description">
            {({ field, meta }) => (
              <label htmlFor="edit-plugin-description-input" className="metaField">
                <Textarea
                  id="edit-plugin-description-input"
                  className="metaLargeTextarea"
                  error={meta.error && meta.touched}
                  aria-labelledby="edit-plugin-description-label"
                  aria-invalid={Boolean(meta.error && meta.touched)}
                  placeholder={`This plugin simulates reader reactions to a chunk of text. The writer must highlight text before using the plugin.

example input: ... just as Josiah grasped Eleanor's hand, she felt a spike of electricity shoot up her spine

example output: Reader 1: "spike of electricity? really? how cliche...`}
                  {...field}
                />
                <ErrorMessage
                  name="description"
                  component="p"
                  className="error"
                />
              </label>
            )}
          </Field>
        </div>

        <div className="metaRow">
          <h3 id="edit-plugin-status-label" className={`metaLabel ${metaLabelAlignmentClassNames.audience}`}>Who can use the plugin?</h3>
          <Field name="status">
            {({ field, form }) => (
              <div className="metaField">
                <StyledSelect
                  inputId="edit-plugin-status-input"
                  aria-labelledby="edit-plugin-status-label"
                  options={pluginStatusOptions}
                  value={pluginStatusOptions.find((option) => option.value === field.value)}
                  onChange={(option) => form.setFieldValue(field.name, option.value)}
                  placeholder="Select who can use the plugin"
                  isSearchable={false}
                  useNeutralSelectedOption
                  formatOptionLabel={formatMetaSelectOptionLabel}
                  showFullFormatInValue={false}
                  styleObjectOverrides={metaSelectStyleOverrides}
                />
              </div>
            )}
          </Field>
        </div>

        <div className="metaRow">
          <h3 id="edit-plugin-prompt-visibility-label" className={`metaLabel ${metaLabelAlignmentClassNames.audience}`}>Who can view the plugin instructions?</h3>
          <Field name="promptVisibility">
            {({ field, form }) => (
              <div className="metaField">
                <StyledSelect
                  inputId="edit-plugin-prompt-visibility-input"
                  aria-labelledby="edit-plugin-prompt-visibility-label"
                  options={pluginInstructionVisibilityOptions}
                  value={pluginInstructionVisibilityOptions.find((option) => option.value === field.value)}
                  onChange={(option) => form.setFieldValue(field.name, option.value)}
                  placeholder="Select who can view plugin instructions"
                  isSearchable={false}
                  useNeutralSelectedOption
                  formatOptionLabel={formatMetaSelectOptionLabel}
                  showFullFormatInValue={false}
                  styleObjectOverrides={metaSelectStyleOverrides}
                />
              </div>
            )}
          </Field>
        </div>

        {categories && (
          <div className="metaRow metaRow--top">
            <h3 id="edit-plugin-category-label" className={`metaLabel ${metaLabelAlignmentClassNames.category}`}>Category</h3>
            <Field name="categoryId">
              {({ field, form }) => (
                <div className="metaField">
                  <StyledSelect
                    inputId="edit-plugin-category-input"
                    aria-labelledby="edit-plugin-category-label"
                    options={categories}
                    value={categories.find((option) => option.value === field.value)}
                    onChange={(option) => form.setFieldValue(field.name, option.value)}
                    placeholder="Select a category"
                    isSearchable={false}
                    useNeutralSelectedOption
                    formatOptionLabel={formatMetaSelectOptionLabel}
                    showFullFormatInValue={false}
                    styleObjectOverrides={metaSelectStyleOverrides}
                  />
                  <ErrorMessage
                    name="categoryId"
                    component="p"
                    className="error"
                  />
                </div>
              )}
            </Field>
          </div>
        )}
      </div>

      <h2 className="instructionsSectionHeading">Plugin Instructions</h2>

      {values.blocks.map((block) => {
        if (block.blockType === "inference") {
          inferenceBlockNumber++;
          return (
            <InferenceBlockEditor
              key={block.id}
              block={block}
              remove={inferenceBlockNumber === 2 ? removeSecondStage : null}
              number={inferenceBlockNumber}
            />
          );
        }
        return null;
      })}
      {inferenceBlocks.length === 1 && (
        <div className="addStepRow">
          <SudoButton variant="tertiary" onClick={addPromptStage} leftIcon="/assets/add.svg">
            Add a Step
          </SudoButton>
          <p className="addStepHint">Run the result through a second set of instructions</p>
        </div>
      )}
      <InputSettings />
    </Container>
  );
}
